import React, { useCallback, useMemo } from "react";
import { useGameRoyalties, useUserCurrencyFormatter } from "@seabrookstudios/pitch2table";
import { BarPlot } from "@mui/x-charts/BarChart";
import { LinePlot } from "@mui/x-charts/LineChart";
import { ChartContainer } from "@mui/x-charts/ChartContainer";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import { royaltyToYear } from "@seabrookstudios/pitch2table-core";

/**
 * Format numbers like 1K, 10K, 1M.
 * @param {number|null|undefined} value
 */
const formatCompactNumber = (value) => {
  if (value === null || value === undefined) return "";
  if (!Number.isFinite(value)) return "";

  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";

  if (abs >= 1_000_000_000) return `${sign}${Math.round(abs / 1_000_000_000)}B`;
  if (abs >= 1_000_000) return `${sign}${Math.round(abs / 1_000_000)}M`;
  if (abs >= 1_000) return `${sign}${Math.round(abs / 1_000)}K`;
  return `${value}`;
};

/**
 * Royalties for a single game, broken down by year with units.
 *
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId|undefined} props.gameId
 */
export const RoyaltiesByYearForGameChart = ({ gameId }) => {
  const { data: royalties = [], isLoading } = useGameRoyalties(gameId);
  const formatUserCurrency = useUserCurrencyFormatter();

  const { axisYears, royaltiesByYear, unitsByYear } = useMemo(() => {
    if (!gameId) return { axisYears: [], royaltiesByYear: [], unitsByYear: [] };

    /** @type {Record<string, { total: number, units: number }>} */
    const byYear = {};

    for (const r of royalties) {
      const year = royaltyToYear(r);
      if (!year) continue;
      byYear[year] = byYear[year] || { total: 0, units: 0 };
      byYear[year].total += r.convertedAmount || 0;
      byYear[year].units += r.units || 0;
    }

    const axisYears = Object.keys(byYear).sort(); // yyyy sorts lexicographically OK
    const royaltiesByYear = axisYears.map((y) => byYear[y].total);
    const unitsByYear = axisYears.map((y) => byYear[y].units);

    return { axisYears, royaltiesByYear, unitsByYear };
  }, [royalties]);

  const Chart = useCallback(() => {
    if (isLoading) {
      return (
        <Box height={200} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </Box>
      );
    }

    if (axisYears.length === 0) {
      return null;
    }

    return (
      <ChartContainer
        height={200}
        series={[
          {
            type: "bar",
            data: royaltiesByYear,
            yAxisId: "royalties",
            label: "Royalties",
            valueFormatter: (v) => formatUserCurrency(v),
          },
          {
            type: "line",
            data: unitsByYear,
            yAxisId: "units",
            label: "Units",
          },
        ]}
        xAxis={[
          {
            scaleType: "band",
            data: axisYears,
          },
        ]}
        yAxis={[
          {
            id: "royalties",
            position: "left",
            valueFormatter: (v) => formatCompactNumber(v),
          },
          {
            id: "units",
            position: "right",
            label: "Units",
            valueFormatter: (v) => formatCompactNumber(v),
          },
        ]}
      >
        <BarPlot />
        <LinePlot />
        <ChartsXAxis />
        <ChartsYAxis axisId="royalties" />
        <ChartsYAxis axisId="units" />
        <ChartsTooltip />
      </ChartContainer>
    );
  }, [axisYears, isLoading, royaltiesByYear, unitsByYear, formatUserCurrency]);

  return (
    <Box>
      <Chart />
      <Divider />
    </Box>
  );
};
